import { useTranslation } from 'react-i18next';

import {
    CustomerNameCell,
    DealCheckboxCell,
    DealNameCell,
    DealObjectCell,
    DealTableCell,
    useDealsTable,
} from '@crm/entities/deal';
import { InWorkOrFinishDeal, PostponeDeal, ReturnDealsToPipeline } from '@crm/features';
import CalendarCheckOutIcon from '@crm/shared/assets/icons/calendar-check-out.svg?react';
import CircleArrowRightIcon from '@crm/shared/assets/icons/circle-arrow-right.svg?react';
import LinkBackwardIcon from '@crm/shared/assets/icons/link-backward.svg?react';
import { cn, DateHelpers, getPluralized, tw } from '@crm/shared/lib';
import {
    Button,
    SelectionActionsBar,
    SortableTableHead,
    SvgIcon,
    Table,
    TableBody,
    TableHead,
    TableHeader,
    TableRow,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@crm/shared/ui';

import { LostReasonCell } from './LostReasonCell';
import { DEALS_TABLE_SORT_FIELDS } from '../../model/consts';

import type {
    TLostDealsData,
    TLostDealsSortDirection,
    TLostDealsSortField,
} from '@crm/entities/deal';

interface DealsTableProps {
    allowClear: boolean;
    dealItems: TLostDealsData['items'];
    direction: TLostDealsSortDirection | null;
    onSortChange: (sortState: {
        direction: TLostDealsSortDirection | null;
        sort: TLostDealsSortField | null;
    }) => void;
    pipelineColumns: TLostDealsData['pipelineColumnsData']['columns'];
    sort: TLostDealsSortField | null;
}

export const DealsTable = ({
    allowClear,
    dealItems,
    direction,
    onSortChange,
    pipelineColumns,
    sort,
}: DealsTableProps) => {
    const { t } = useTranslation();
    // Trade-off: для каждой строки создаются экземпляры feature-компонентов с оверлеями.
    // Если количество одновременно отрендеренных строк заметно вырастет, действия стоит
    // перевести на один управляемый оверлей на уровне таблицы.
    const tableGridTemplateColumnsStyles = tw(
        'grid-cols-[24px_minmax(110px,1fr)_200px_200px_minmax(100px,1fr)_140px_100px]'
    );
    const {
        clearSelectedDeals,
        handleRowClick,
        handleRowKeyDown,
        handleSelectAllDeals,
        handleSelectDeal,
        isAllDealsSelected,
        isSomeDealsSelected,
        selectedDealIds,
    } = useDealsTable({ dealItems });

    return (
        <div className="relative h-full">
            <Table containerClassName="h-full overflow-y-auto" className="block min-w-240">
                <TableHeader className="sticky top-0 z-10 block bg-white [&_th]:h-auto [&_th]:py-2 [&_th]:text-xs [&_th]:font-normal [&_th]:text-smtr-main-tertiary-100 [&_tr]:border-0">
                    <TableRow
                        className={cn(
                            'grid items-center gap-x-3 hover:bg-transparent',
                            tableGridTemplateColumnsStyles
                        )}
                    >
                        <DealCheckboxCell
                            variant="header"
                            checked={isAllDealsSelected}
                            indeterminate={isSomeDealsSelected && !isAllDealsSelected}
                            aria-label={t('crm.common.dealsTable.selectAllAriaLabel')}
                            onCheckedChange={handleSelectAllDeals}
                        />
                        <TableHead className="pl-1">
                            {t('crm.lostPage.table.header.dealName')}
                        </TableHead>
                        <TableHead className="px-1">
                            {t('crm.lostPage.table.header.customerName')}
                        </TableHead>
                        <TableHead className="px-1">
                            {t('crm.lostPage.table.header.object')}
                        </TableHead>
                        <TableHead className="px-1">
                            {t('crm.lostPage.table.header.lostReason')}
                        </TableHead>
                        {/* <TableHead>Комментарий</TableHead> */}
                        <SortableTableHead
                            activeSort={sort}
                            allowClear={allowClear}
                            direction={direction}
                            sortField={DEALS_TABLE_SORT_FIELDS.lostAt}
                            onSortChange={onSortChange}
                        >
                            {t('crm.lostPage.table.header.lostAt')}
                        </SortableTableHead>
                        <TableHead className="px-1 text-right">
                            {t('crm.lostPage.table.header.actions')}
                        </TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody className="grid gap-y-1 [&_td]:py-2">
                    {dealItems.map((deal) => (
                        <TableRow
                            key={deal.id}
                            className={cn(
                                'grid h-10 cursor-pointer items-center gap-x-3 rounded-md border-0 hover:bg-smtr-main-bgGray-50 data-[state=selected]:bg-smtr-main-accent-50',
                                tableGridTemplateColumnsStyles
                            )}
                            data-state={selectedDealIds.has(deal.id) ? 'selected' : undefined}
                            tabIndex={0}
                            aria-label={t('crm.common.dealsTable.row.openAriaLabel', {
                                dealName: deal.name,
                            })}
                            onClick={(event) => handleRowClick(event, deal.id)}
                            onKeyDown={(event) => handleRowKeyDown(event, deal.id)}
                        >
                            <DealCheckboxCell
                                checked={selectedDealIds.has(deal.id)}
                                aria-label={t('crm.common.dealsTable.row.selectAriaLabel', {
                                    dealName: deal.name,
                                })}
                                onCheckedChange={(checked) => handleSelectDeal(deal.id, checked)}
                            />
                            <DealNameCell dealName={deal.name} />
                            <CustomerNameCell
                                customerName={deal.customerName}
                                className="max-w-100"
                            />
                            <DealObjectCell objectType={deal.objectType} />
                            <LostReasonCell lostReason={deal.reason} />
                            <DealTableCell isEmpty={!deal.lostAt} className="px-1">
                                {DateHelpers.formatDisplayDate(new Date(deal.lostAt))}
                            </DealTableCell>
                            <DealTableCell className="px-1">
                                <div className="flex items-center justify-end gap-1">
                                    <Tooltip>
                                        <ReturnDealsToPipeline
                                            dealIds={[deal.id]}
                                            columns={pipelineColumns}
                                            trigger={
                                                <TooltipTrigger
                                                    render={
                                                        <Button
                                                            type="button"
                                                            variant="ghost"
                                                            size="icon-sm"
                                                            className="size-6 rounded-[6px] bg-blue-50 text-blue-500 hover:bg-blue-100 hover:text-blue-500"
                                                            aria-label={t(
                                                                'crm.common.actions.returnToPipeline'
                                                            )}
                                                        >
                                                            <SvgIcon
                                                                icon={LinkBackwardIcon}
                                                                size="16"
                                                            />
                                                        </Button>
                                                    }
                                                />
                                            }
                                        />
                                        <TooltipContent>
                                            {t('crm.common.actions.returnToPipeline')}
                                        </TooltipContent>
                                    </Tooltip>
                                    <Tooltip>
                                        <PostponeDeal
                                            dealIds={[deal.id]}
                                            trigger={
                                                <TooltipTrigger
                                                    render={
                                                        <Button
                                                            type="button"
                                                            variant="ghost"
                                                            size="icon-sm"
                                                            className="size-6 rounded-[6px] bg-smtr-border-100 text-smtr-main-tertiary-100"
                                                            aria-label={t(
                                                                'crm.common.actions.postponeDeal'
                                                            )}
                                                        >
                                                            <SvgIcon
                                                                icon={CalendarCheckOutIcon}
                                                                size="16"
                                                                className="text-smtr-main-green-100"
                                                            />
                                                        </Button>
                                                    }
                                                />
                                            }
                                        />
                                        <TooltipContent>
                                            {t('crm.common.actions.postponeDeal')}
                                        </TooltipContent>
                                    </Tooltip>
                                    <Tooltip>
                                        <InWorkOrFinishDeal
                                            dealIds={[deal.id]}
                                            invalidationScope="lost"
                                            trigger={
                                                <TooltipTrigger
                                                    render={
                                                        <Button
                                                            type="button"
                                                            variant="ghost"
                                                            size="icon-sm"
                                                            className="size-6 rounded-[6px] bg-smtr-border-100 text-smtr-main-tertiary-100"
                                                            aria-label={t(
                                                                'crm.common.actions.sendToWork'
                                                            )}
                                                        >
                                                            <SvgIcon
                                                                icon={CircleArrowRightIcon}
                                                                size="16"
                                                            />
                                                        </Button>
                                                    }
                                                />
                                            }
                                        />
                                        <TooltipContent>
                                            {t('crm.common.actions.sendToWork')}
                                        </TooltipContent>
                                    </Tooltip>
                                </div>
                            </DealTableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            {selectedDealIds.size > 0 && (
                <SelectionActionsBar
                    label={t('crm.common.dealsTable.selection.label', {
                        deals: getPluralized(selectedDealIds.size, {
                            one: t('crm.common.plurals.deal.one'),
                            few: t('crm.common.plurals.deal.few'),
                            many: t('crm.common.plurals.deal.many'),
                        }),
                    })}
                    onClose={clearSelectedDeals}
                >
                    <ReturnDealsToPipeline
                        dealIds={Array.from(selectedDealIds)}
                        columns={pipelineColumns}
                        trigger={
                            <Button type="button" className="bg-primary">
                                {t('crm.common.actions.returnToPipeline')}
                            </Button>
                        }
                    />
                    <PostponeDeal
                        dealIds={Array.from(selectedDealIds)}
                        onSubmit={clearSelectedDeals}
                        trigger={
                            <Button
                                type="button"
                                className="bg-smtr-main-green-100 hover:bg-smtr-main-green-200"
                            >
                                {t('crm.common.actions.postponeDeal')}
                            </Button>
                        }
                    />
                    <InWorkOrFinishDeal
                        dealIds={Array.from(selectedDealIds)}
                        invalidationScope="lost"
                        onSuccess={clearSelectedDeals}
                        trigger={
                            <Button type="button">{t('crm.common.actions.sendToWork')}</Button>
                        }
                    />
                </SelectionActionsBar>
            )}
        </div>
    );
};
